import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Eye, Trash2, Building2, Filter, Briefcase } from "lucide-react";
import { InterViewSlotService } from "@/services/InterViewSlot";
import { vacancyService } from "@/services/vacancyService";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import DialogBox from "@/components/Common/DialogBox";
import Pagination from "@/components/Common/Pagination";
import usePaginationStore from "@/hooks/stores/usePaginationStore";
import Dropdown from "@/components/ui/Dropdown";
import { useAllVacanciesByCompanyId } from "@/hooks/queries/usevacancy";
import { useCampaignByCompanyId } from "@/hooks/queries/campaigns";

/**
 * Reusable interview slots schedules table.
 * Used on /interviews-slots and Company View → Display Slots.
 */
const InterviewSlotsList = ({
  title = "Interview Slots",
  description = "Manage your interview availability slots and recruitment schedules.",
  showHeader = true,
  showCreateButton = true,
  companyId = null,
  embedded = false,
  paginationKey = "interview-slots-table",
  requireVacancySelection = false,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToastStore;
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [selectedVacancyId, setSelectedVacancyId] = useState(null);
  const [selectedStatusId, setSelectedStatusId] = useState(null);
  const { getPerPage, setPerPage } = usePaginationStore();
  const [itemsPerPage, setItemsPerPage] = useState(() =>
    getPerPage(paginationKey),
  );
  const [currentPage, setCurrentPage] = useState(1);

  const isDisplaySlots = requireVacancySelection && !!companyId;

  const { data: campaignsData, isLoading: isCampaignsLoading } =
    useCampaignByCompanyId(isDisplaySlots ? companyId : null, {
      per_page: 1000,
    });

  // All vacancies for selected campaign (draft / published / archived)
  const { data: campaignVacanciesData, isLoading: isCampaignVacanciesLoading } =
    useQuery({
      queryKey: [
        "vacancy",
        "display-slots",
        companyId,
        selectedCampaignId,
        "all",
      ],
      queryFn: () =>
        vacancyService.getCompanyVacancies(companyId, {
          campaign_id: selectedCampaignId,
          per_page: 1000,
          page: 1,
          status: [0, 1, 2],
        }),
      enabled: isDisplaySlots && !!companyId && !!selectedCampaignId,
    });

  const { data: companyVacanciesData, isLoading: isVacanciesLoading } =
    useAllVacanciesByCompanyId(!isDisplaySlots ? companyId : null);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    itemsPerPage,
    companyId,
    selectedCampaignId,
    selectedVacancyId,
    selectedStatusId,
  ]);

  useEffect(() => {
    setSelectedCampaignId(null);
    setSelectedVacancyId(null);
    setSelectedStatusId(null);
  }, [companyId]);

  const normalizeSchedulesResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (response?.data && Array.isArray(response.data)) return response.data;
    return [];
  };

  const buildScheduleFilters = () => ({
    vacancyId:
      selectedVacancyId != null && selectedVacancyId !== ""
        ? selectedVacancyId
        : undefined,
    vacancyStatusId:
      selectedStatusId != null && selectedStatusId !== ""
        ? selectedStatusId
        : undefined,
    companyId: companyId || undefined,
  });

  const fetchSchedules = async (filters = {}) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await InterViewSlotService.getSchedules(filters);
      setSchedules(normalizeSchedulesResponse(response));
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;

      console.error("Error fetching schedules:", error);
      setSchedules([]);
      setErrorMessage(apiMessage || "Failed to load interview slots.");
    } finally {
      setIsLoading(false);
    }
  };

  // Display Slots: fetch table data when vacancy / status changes
  useEffect(() => {
    if (!requireVacancySelection) return;

    if (selectedVacancyId == null || selectedVacancyId === "") {
      setSchedules([]);
      setErrorMessage(null);
      setIsLoading(false);
      return;
    }

    fetchSchedules(buildScheduleFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireVacancySelection, selectedVacancyId, selectedStatusId, companyId]);

  // Global /interviews-slots page: load on mount; refetch when filters change
  useEffect(() => {
    if (requireVacancySelection) return;

    fetchSchedules(buildScheduleFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireVacancySelection, selectedVacancyId, selectedStatusId, companyId]);

  const handleView = (row) => {
    const vacancyId = row.vacancy_id;
    const statusId = row.vacancy_status_id;
    navigate(`/interviews-slots/${vacancyId}/${statusId}`);
  };

  const openDeleteConfirm = (row) => {
    const vacancyId = row.vacancy_id;
    const statusId = row.vacancy_status_id;
    if (!vacancyId || !statusId) return;
    setDeleteConfirmRow(row);
  };

  const closeDeleteConfirm = () => {
    if (isDeletingSchedule) return;
    setDeleteConfirmRow(null);
  };

  const confirmDeleteSlot = async () => {
    const row = deleteConfirmRow;
    if (!row) return;
    const vacancyId = row.vacancy_id;
    const statusId = row.vacancy_status_id;
    if (!vacancyId || !statusId) return;

    const key = `${vacancyId}-${statusId}`;
    setIsDeletingSchedule(true);
    setDeletingKey(key);
    try {
      await InterViewSlotService.deleteVacancyStatusBlocks(vacancyId, statusId);
      showToast({ message: "Schedule deleted successfully.", type: "success" });
      setDeleteConfirmRow(null);
      await fetchSchedules(buildScheduleFilters());
    } catch (error) {
      showToast({
        message: error.message || "Failed to delete schedule.",
        type: "error",
      });
    } finally {
      setIsDeletingSchedule(false);
      setDeletingKey(null);
    }
  };

  const companyFilteredSchedules = useMemo(() => {
    if (companyId == null || companyId === "") return schedules;
    const id = String(companyId);
    return schedules.filter(
      (row) =>
        String(row?.company?.id ?? row?.company_id ?? "") === id,
    );
  }, [schedules, companyId]);

  const campaignOptions = useMemo(() => {
    const list =
      campaignsData?.campaigns ||
      campaignsData?.data ||
      (Array.isArray(campaignsData) ? campaignsData : []);
    return list
      .map((campaign) => ({
        label: campaign.title || `Campaign #${campaign.id}`,
        value: campaign.id,
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [campaignsData]);

  const selectedCampaignOption = useMemo(() => {
    if (selectedCampaignId == null || selectedCampaignId === "") return null;
    return (
      campaignOptions.find(
        (opt) => String(opt.value) === String(selectedCampaignId),
      ) || null
    );
  }, [campaignOptions, selectedCampaignId]);

  const vacancyOptions = useMemo(() => {
    const byId = new Map();

    const addVacancy = (id, title, vacancy) => {
      if (id == null) return;
      const key = String(id);
      const existing = byId.get(key);
      // Prefer richer vacancy objects (with vacancy_statuses) from API
      if (
        existing?.vacancy?.vacancy_statuses?.length &&
        !vacancy?.vacancy_statuses?.length
      ) {
        return;
      }
      byId.set(key, {
        label: title || existing?.label || `Vacancy #${id}`,
        value: id,
        vacancy: vacancy || existing?.vacancy || { id, title },
      });
    };

    if (isDisplaySlots) {
      const fromCampaign =
        campaignVacanciesData?.vacancies ||
        campaignVacanciesData?.data?.vacancies ||
        campaignVacanciesData?.data ||
        (Array.isArray(campaignVacanciesData) ? campaignVacanciesData : null);

      if (Array.isArray(fromCampaign)) {
        fromCampaign.forEach((vacancy) => {
          addVacancy(vacancy.id, vacancy.title, vacancy);
        });
      }
    } else {
      const fromApi =
        companyVacanciesData?.vacancies ||
        companyVacanciesData?.data ||
        (Array.isArray(companyVacanciesData) ? companyVacanciesData : null);

      if (Array.isArray(fromApi)) {
        fromApi.forEach((vacancy) => {
          addVacancy(vacancy.id, vacancy.title, vacancy);
        });
      }

      companyFilteredSchedules.forEach((row) => {
        const id = row?.vacancy_id ?? row?.vacancy?.id;
        const title = row?.vacancy?.title;
        addVacancy(id, title, row?.vacancy);
      });
    }

    return Array.from(byId.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [
    isDisplaySlots,
    campaignVacanciesData,
    companyVacanciesData,
    companyFilteredSchedules,
  ]);

  const selectedVacancyOption = useMemo(() => {
    if (selectedVacancyId == null || selectedVacancyId === "") return null;
    return (
      vacancyOptions.find(
        (opt) => String(opt.value) === String(selectedVacancyId),
      ) || null
    );
  }, [vacancyOptions, selectedVacancyId]);

  const selectedVacancy = selectedVacancyOption?.vacancy || null;

  const statusOptions = useMemo(() => {
    if (selectedVacancy?.vacancy_statuses?.length) {
      return selectedVacancy.vacancy_statuses.map((vs) => ({
        label: vs?.status?.name || `Status #${vs.id}`,
        value: vs.id,
      }));
    }

    const source =
      selectedVacancyId == null || selectedVacancyId === ""
        ? companyFilteredSchedules
        : companyFilteredSchedules.filter(
            (row) =>
              String(row?.vacancy_id ?? row?.vacancy?.id ?? "") ===
              String(selectedVacancyId),
          );

    const byId = new Map();
    source.forEach((row) => {
      const id = row?.vacancy_status_id ?? row?.vacancy_status?.id;
      const name = row?.vacancy_status?.status?.name;
      if (id != null && !byId.has(String(id))) {
        byId.set(String(id), {
          label: name || `Status #${id}`,
          value: id,
        });
      }
    });
    return Array.from(byId.values());
  }, [selectedVacancy, selectedVacancyId, companyFilteredSchedules]);

  const selectedStatusOption = useMemo(() => {
    if (selectedStatusId == null || selectedStatusId === "") return null;
    return (
      statusOptions.find(
        (opt) => String(opt.value) === String(selectedStatusId),
      ) || null
    );
  }, [statusOptions, selectedStatusId]);

  const displayedSchedules = useMemo(() => {
    const toTime = (d) => {
      if (!d) return 0;
      const t = new Date(d).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    if (
      requireVacancySelection &&
      (selectedVacancyId == null || selectedVacancyId === "")
    ) {
      return [];
    }

    // Backend already filters by vacancy_id / vacancy_status_id / company_id
    return [...companyFilteredSchedules].sort((a, b) => {
      const createdDiff = toTime(b?.created_date) - toTime(a?.created_date);
      if (createdDiff !== 0) return createdDiff;

      const fromDiff = toTime(b?.from_date) - toTime(a?.from_date);
      if (fromDiff !== 0) return fromDiff;

      const statusDiff =
        (b?.vacancy_status_id ?? 0) - (a?.vacancy_status_id ?? 0);
      if (statusDiff !== 0) return statusDiff;

      return (b?.vacancy_id ?? 0) - (a?.vacancy_id ?? 0);
    });
  }, [companyFilteredSchedules, selectedVacancyId, requireVacancySelection]);

  const isCampaignSelected =
    selectedCampaignId != null && selectedCampaignId !== "";
  const isVacancySelected =
    selectedVacancyId != null && selectedVacancyId !== "";
  const waitingForCampaign = isDisplaySlots && !isCampaignSelected;
  const waitingForVacancy =
    requireVacancySelection && isCampaignSelected && !isVacancySelected;
  const waitingForSelection = waitingForCampaign || waitingForVacancy;
  const showTableLoading =
    isLoading ||
    (isDisplaySlots && isCampaignsLoading) ||
    (isDisplaySlots && isCampaignSelected && isCampaignVacanciesLoading) ||
    (!isDisplaySlots && !!companyId && isVacanciesLoading);

  // Backend already filters by vacancy_id / vacancy_status_id / company_id
  const filteredSchedules = displayedSchedules;

  const totalPages = useMemo(() => {
    const per = Number(itemsPerPage) || 1;
    return Math.max(1, Math.ceil(filteredSchedules.length / per));
  }, [filteredSchedules.length, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedSchedules = useMemo(() => {
    const per = Number(itemsPerPage) || 1;
    const start = (currentPage - 1) * per;
    return filteredSchedules.slice(start, start + per);
  }, [filteredSchedules, currentPage, itemsPerPage]);

  const handlePerPageChange = (option) => {
    const next = option?.value ?? option;
    setItemsPerPage(next);
    setPerPage(paginationKey, next);
    setCurrentPage(1);
  };

  const filtersSection = (
    <div className="flex flex-row flex-wrap justify-start items-center gap-4">
      {isDisplaySlots && (
        <Dropdown
          options={campaignOptions}
          value={selectedCampaignOption}
          onChange={(selected) => {
            const next = selected?.value;
            setSelectedCampaignId(next === "" || next == null ? null : next);
            setSelectedVacancyId(null);
            setSelectedStatusId(null);
          }}
          placeholder="Select Campaign"
          icon={<Briefcase className="w-5 h-5 text-foreground flex-shrink-0" />}
          searchable
          isLoading={isCampaignsLoading}
          loadingText="Loading Campaigns..."
          className="w-64 shrink-0"
          dropdownWidth="w-64"
          renderOption={(option) => (
            <p className="text-foreground">{option.label}</p>
          )}
        />
      )}
      <Dropdown
        options={vacancyOptions}
        value={selectedVacancyOption}
        onChange={(selected) => {
          const next = selected?.value;
          setSelectedVacancyId(next === "" || next == null ? null : next);
          setSelectedStatusId(null);
        }}
        placeholder={
          isDisplaySlots && !isCampaignSelected
            ? "Select Campaign first"
            : "Select Vacancy"
        }
        icon={<Building2 className="w-5 h-5 text-foreground flex-shrink-0" />}
        searchable
        allowAll={!requireVacancySelection}
        allLabel="All Vacancies"
        disabled={isDisplaySlots && !isCampaignSelected}
        isLoading={
          isDisplaySlots && isCampaignSelected && isCampaignVacanciesLoading
        }
        loadingText="Loading Vacancies..."
        className="w-64 shrink-0"
        dropdownWidth="w-64"
        renderOption={(option) => (
          <p className="text-foreground">{option.label}</p>
        )}
      />
      {(!requireVacancySelection || isVacancySelected) && (
        <Dropdown
          options={statusOptions}
          value={selectedStatusOption}
          onChange={(selected) => {
            const next = selected?.value;
            setSelectedStatusId(next === "" || next == null ? null : next);
          }}
          placeholder="Status"
          icon={<Filter className="w-5 h-5 text-foreground flex-shrink-0" />}
          searchable
          allowAll
          allLabel="All Statuses"
          className="w-64 shrink-0"
          dropdownWidth="w-64"
          renderOption={(option) => (
            <p className="text-foreground">{option.label}</p>
          )}
        />
      )}
    </div>
  );

  const content = (
    <>
      {showHeader && (
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1
                className={`text-foreground font-semibold font-['Inter'] leading-8 ${
                  embedded ? "text-xl" : "text-2xl"
                }`}
              >
                {title}
              </h1>
              {description && (
                <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                  {description}
                </p>
              )}
            </div>
            {showCreateButton && (
              <button
                onClick={() => navigate("/interviews-slots/create")}
                className="bg-primary-dark h-[36px] text-white px-4 py-2.5 rounded-lg shadow-sm font-semibold transition-all hover:bg-slate-900 flex items-center gap-2"
                id="create-slots-btn"
              >
                <Plus className="w-4 h-4" />
                Create Event Slots
              </button>
            )}
          </div>
          {filtersSection}
        </div>
      )}

      {!showHeader && <div className="mb-4">{filtersSection}</div>}

      <div className="bg-card shadow rounded-lg overflow-hidden border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Company Name
              </th>
              <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Vacancy Title
              </th>
              <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Status Name
              </th>
              <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Available Days
              </th>
              <th className="px-6 py-5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {showTableLoading ? (
              <tr>
                <td
                  colSpan="5"
                  className="px-6 py-12 text-center text-muted-foreground"
                >
                  {embedded ? (
                    <div className="flex items-center justify-center w-full">
                      <Loader
                        bgColor="transparent"
                        containerClassName="!bg-transparent w-fit mx-auto p-2"
                        className="w-8 h-8"
                      />
                    </div>
                  ) : (
                    <Loader bgColor="transparent" />
                  )}
                </td>
              </tr>
            ) : waitingForSelection ? (
              <tr>
                <td
                  colSpan="5"
                  className="px-6 py-8 text-center text-muted-foreground"
                >
                  {waitingForCampaign
                    ? "Please select campaign"
                    : "Please select vacancy"}
                </td>
              </tr>
            ) : errorMessage ? (
              <tr>
                <td
                  colSpan="5"
                  className="px-6 py-8 text-center text-red-600"
                >
                  {errorMessage}
                </td>
              </tr>
            ) : filteredSchedules.length === 0 ? (
              <tr>
                <td
                  colSpan="5"
                  className="px-6 py-8 text-center text-muted-foreground"
                >
                  No slots found.
                </td>
              </tr>
            ) : (
              paginatedSchedules.map((row) => (
                <tr
                  key={`${row?.vacancy_status_id ?? "status"}-${row?.vacancy_id ?? "vacancy"}-${row?.from_date ?? "from"}-${row?.to_date ?? "to"}-${row?.created_date ?? "created"}`}
                  className="hover:bg-muted transition-colors duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                    {row.company?.name || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {row.vacancy?.title || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className="px-2 py-1 rounded-full text-xs font-semibold"
                      style={{
                        backgroundColor:
                          row.vacancy_status?.status?.bg_color + "20",
                        color: row.vacancy_status?.status?.bg_color || "#333",
                      }}
                    >
                      {row.vacancy_status?.status?.name || "-"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {row.blocks_count || 0}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end items-center space-x-3">
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                        onClick={() => handleView(row)}
                        title="View Details"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        aria-label="Delete schedule"
                        disabled={
                          deletingKey ===
                          `${row.vacancy_id}-${row.vacancy_status_id}`
                        }
                        className="text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                        onClick={() => openDeleteConfirm(row)}
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <DialogBox
        isOpen={!!deleteConfirmRow}
        onClose={closeDeleteConfirm}
        title="Delete Interview Slot"
        className="w-[40%] max-md:w-[60%]"
        disableBackdropClick={isDeletingSchedule}
        actionButton={
          <button
            type="button"
            onClick={confirmDeleteSlot}
            disabled={isDeletingSchedule}
            className="inline-flex w-full justify-center items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:pointer-events-none"
          >
            {isDeletingSchedule && (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
            )}
            {isDeletingSchedule ? "Deleting…" : "Delete"}
          </button>
        }
      >
        <p className="text-muted-foreground text-sm mt-1 max-w-[390px]">
          Are you sure you want to delete this slot? This will remove all
          interview availability for{" "}
          {deleteConfirmRow ? (
            <>
              <span className="font-medium text-foreground">
                {deleteConfirmRow.vacancy?.title || "this vacancy"}
              </span>
              {deleteConfirmRow.vacancy_status?.status?.name ? (
                <> ({deleteConfirmRow.vacancy_status.status.name})</>
              ) : null}
            </>
          ) : null}
          . This action cannot be undone.
        </p>
      </DialogBox>

      {!waitingForSelection && (
        <div className={embedded ? "pt-4" : "pt-6"}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            per_page={Number(itemsPerPage) || 1}
            onPerPageChange={handlePerPageChange}
          />
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div className="flex flex-col gap-4">{content}</div>;
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12 px-8 mx-auto flex flex-col gap-8">
        {content}
      </div>
    </div>
  );
};

export default InterviewSlotsList;
